import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Notification } from '../assests/ExportImageIcon'
import service from '../services/service'
import endpoints from '../services/endpoints'
import { setList, markRead as markReadAction, markAllRead as markAllReadAction, removeNotification } from '../redux/notifications/notificationsSlice'
import { getNotificationRoute } from '../utils/notificationRoutes'
import './notificationCenter.css'

const formatWhen = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}

const NotificationCenter = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)
    const items = useSelector((state) => state.notifications.items)
    const unreadCount = useSelector((state) => state.notifications.unreadCount)
    const next = useSelector((state) => state.notifications.next)

    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const panelRef = useRef(null)

    const fetchList = (page = 1, append = false) => {
        if (page === 1) setLoading(true)
        else setLoadingMore(true)

        service
            .get(endpoints.notifications.list, { page, page_size: 10 }, { silent: true })
            .then(({ data }) => {
                const notifications = Array.isArray(data?.data?.notifications) ? data.data.notifications : []
                dispatch(setList({
                    notifications,
                    next: data?.next || null,
                    unread_count: data?.data?.unread_count,
                    append,
                }))
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false)
                setLoadingMore(false)
            })
    }

    // Fetch once on login so the badge is populated even before the panel is
    // ever opened (matches the doc's post-login bootstrap step).
    useEffect(() => {
        if (isLoggedIn) fetchList(1, false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        if (open) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    const handleToggle = () => {
        if (!isLoggedIn) return
        const willOpen = !open
        setOpen(willOpen)
        // Socket is best-effort — always refetch on open so the list/badge
        // reflect the true server state.
        if (willOpen) fetchList(1, false)
    }

    const handleMarkAllRead = () => {
        service
            .post(endpoints.notifications.markAllRead, {}, { silent: true })
            .then(() => dispatch(markAllReadAction()))
            .catch(() => {})
    }

    const handleLoadMore = () => {
        if (!next) return
        try {
            const url = new URL(next)
            const page = url.searchParams.get('page') || '2'
            fetchList(Number(page), true)
        } catch {
            // malformed next URL — nothing sensible to do
        }
    }

    const handleItemClick = (item) => {
        if (!item.is_read) {
            service
                .patch(endpoints.notifications.markRead.replace('{id}', item.id), {}, { silent: true })
                .then(() => dispatch(markReadAction(item.id)))
                .catch(() => {})
        }

        const route = getNotificationRoute(item)
        setOpen(false)
        if (route) navigate(route)
    }

    const handleDelete = (e, item) => {
        e.stopPropagation()
        service
            .delete(endpoints.notifications.delete.replace('{id}', item.id), { silent: true })
            .then(() => dispatch(removeNotification(item.id)))
            .catch(() => {})
    }

    return (
        <div className="notification" ref={panelRef}>
            <button type="button" className="notification__bellBtn" onClick={handleToggle} aria-label="Notifications">
                <img src={Notification} alt="Notification" />
                {isLoggedIn && unreadCount > 0 && (
                    <div className="notificationCount">{unreadCount > 99 ? '99+' : unreadCount}</div>
                )}
            </button>

            {open && (
                <div className="notificationCenter__panel">
                    <div className="notificationCenter__header">
                        <span>Notifications</span>
                        {unreadCount > 0 && (
                            <button type="button" onClick={handleMarkAllRead}>Mark all read</button>
                        )}
                    </div>

                    <div className="notificationCenter__list">
                        {loading ? (
                            <p className="notificationCenter__empty">Loading...</p>
                        ) : items.length ? items.map((item) => (
                            <div
                                key={item.id}
                                className={`notificationCenter__item ${item.is_read ? '' : 'unread'}`}
                                onClick={() => handleItemClick(item)}
                            >
                                <div className="notificationCenter__itemBody">
                                    <div className="notificationCenter__itemTitle">{item.title}</div>
                                    <div className="notificationCenter__itemMessage">{item.message}</div>
                                    <div className="notificationCenter__itemTime">{formatWhen(item.created_at)}</div>
                                </div>
                                <button
                                    type="button"
                                    className="notificationCenter__deleteBtn"
                                    onClick={(e) => handleDelete(e, item)}
                                    aria-label="Delete notification"
                                >
                                    ×
                                </button>
                            </div>
                        )) : (
                            <p className="notificationCenter__empty">No notifications yet.</p>
                        )}
                    </div>

                    {next && (
                        <button type="button" className="notificationCenter__loadMore" onClick={handleLoadMore} disabled={loadingMore}>
                            {loadingMore ? 'Loading...' : 'Load more'}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default NotificationCenter
